/**
 * dog-view.js
 */

import React from 'react';
import axios from 'axios';
import makeStyles from '@mui/styles/makeStyles';
import {
  Grid,
  Box,
  Avatar,
  Badge,
  Typography,
  Button as MuiButton,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { isFuture, format, differenceInMonths, formatDistanceStrict, parseISO } from 'date-fns';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import DateDistance from 'components/date-distance';
import Button from 'components/button';
import EditButton from 'components/edit-button';
import DropMenu from 'components/drop-menu';
import ConfirmByTypingDialog from 'components/confirm-dialog/confirm-by-typing-dialog';
import defaultDog from 'assets/images/default-dog.jpg';


const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: theme.spacing(5),
    [theme.breakpoints.down('md')] : {
      padding: theme.spacing(4),
    }
  },
  mainSection: {
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('md')] : {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingTop: theme.spacing(2),
    }
  },
  mainSectionText: {
    alignItems: 'center',
    [theme.breakpoints.down('md')] : {
      marginLeft: theme.spacing(3),
      alignItems: 'normal',
    }
  },
  avatar: {
    width: theme.spacing(16),
    height: theme.spacing(16),
    marginBottom: theme.spacing(2),
    // cursor: 'pointer',
    [theme.breakpoints.down('md')] : {
      width: theme.spacing(14),
      height: theme.spacing(14),
      marginBottom: 0,
    },
    [theme.breakpoints.down('sm')] : {
      width: theme.spacing(12),
      height: theme.spacing(12),
    },
    '& img': {
      opacity: 1,
      transition: `200ms ${theme.transitions.easing.easeOut}`,
      borderRadius: '50%', // hack for iOS which shows square avatar on tap
    }
  },
  editPhotoBadge: {
    '&:hover img': {
      opacity: 0.5,
    },
    '&:hover button': {
      opacity: 1
    },
  },
  editPhotoButton: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    borderRadius: '50%',
    opacity: 0,
  },
  subTitle: {
    color: theme.palette.grey.colors[4],
    textAlign: 'center',
    [theme.breakpoints.down('md')] : {
      textAlign: 'left',
    },
  },
  defaultDogIcon: {
    marginLeft: theme.spacing(1),
    color: theme.palette.secondary.colors[4],
    fontSize: '1rem',
  },
  info: {
    '& .pt-icon': {
      color: theme.palette.grey.colors[4],
    },
  },
  docsButton: {
    textTransform: 'none',
    padding: 0,
    fontWeight: 'normal',
    textAlign: 'left',
    lineHeight: 1.2,
  },
  floating: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')] : {
      padding: theme.spacing(1),
    },
  },
  chip: {
    marginTop: theme.spacing(2),
    width: theme.spacing(10),
  },
}));

export default function DogView(props) {
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { url } = useRouteMatch();
  const history = useHistory();  
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('general');
  const [patrolTypes] = React.useState(Types.Patrol.Training.entries(t));
  const [odorTypes] = React.useState(Types.OdorPrimary.entries(t));
  const [dog, setDog] = React.useState();
  const [confirmDelete, setConfirmDelete] = React.useState();
  const [loading, setLoading] = React.useState(false);
  const isReadOnly = user.role.isTrainer;
  const avatarBase = dog && (
    <Avatar 
      className={classes.avatar}
      alt="Dog Profile Photo"
      src={dog.photo ? dog.photo : defaultDog}
    />
  );
  const avatar = !avatarBase
    ? ''
    : isReadOnly
      ? avatarBase
      : <Badge
          overlap="circular"
          className={classes.editPhotoBadge}
          onClick={() => performAction('edit-photo')}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          badgeContent={
            <Button 
              variant="contained"
              size="small" 
              className={classes.editPhotoButton}
            >
              <Icon type='edit' />
            </Button>
          }
        >
          {avatarBase}
        </Badge>;


  // after mount, get page details
  React.useEffect(() => {
    if (props.dog) {
      // calculate total documents
      const documentCount = props.dog.documents.reduce((sum, cat) => {
        return sum + cat.files.length;
      }, 0);
      setDog({...props.dog, documentCount});
    } else if (props.id) {
      // TODO: query dog details by id
    }
  }, [props.dog, props.id]);


  const displayRecordsRange = (dateStarted, dateRetired) => {
    const endDate = Math.min(dateRetired ? dateRetired : new Date(), new Date());
    const totalMonths = differenceInMonths(endDate, dateStarted);
    return `RECORDS: 
      ${format(dateStarted, 'MMMM dd, yyyy')} 
      to 
      ${(!dateRetired || dateRetired > new Date()) ? 'Now' : format(dateRetired, 'MMMM dd, yyyy')}
      (${formatDistanceStrict(dateStarted, endDate, {roundingMethod: 'floor'})}${totalMonths > 12 ? `, ${totalMonths % 12} months` : ''})`;
  }


  const listAttributes = (defs, attributes) => {
    return defs
      .filter(def => attributes.includes(def.id))
      .map(def => def.name)
      .join(', ');
  }


  const performAction = (action) => {
    if (['edit-dog', 'edit-photo', 'vaccinations', 'manage-documents'].includes(action)) {
      props.setDogEdit && props.setDogEdit(props.dog);
      let drawerUrl = `${url}/${dog.id}/edit`;
      switch (action) {
        default:
        case 'edit-dog':
          drawerUrl = `${drawerUrl}/details`;
          break;
        case 'edit-photo':
          drawerUrl = `${drawerUrl}/photo`;
          break;
        case 'vaccinations':
          drawerUrl = `${drawerUrl}/vaccinations`;
          break;
        case 'manage-documents':
          drawerUrl = `${drawerUrl}/docs`;
          break;
      }

      history.push(drawerUrl);

    } else if (action === 'make-default') {
      makeDefault();
    } else if (action === 'delete-dog') {
      deleteDog();      
    }
  }


  const makeDefault = () => {
    setLoading(true);
    axios.put('/dogs/' + dog.id, {...dog, isDefault: true})
      .then(response => {
        setLoading(false);
        const newDog = response.data;
        const updatedDog = {
          ...newDog,
          dateBirth: newDog.dateBirth ? parseISO(newDog.dateBirth) : null,
          dateRetired: newDog.dateRetired ? parseISO(newDog.dateRetired) : null,
          dateStarted: newDog.dateStarted ? parseISO(newDog.dateStarted) : null,
        }
        
        setDog(updatedDog);
        if (props.onSave) {
          props.onSave(updatedDog);
        }
      });
  }


  const confirmDeleteText = () => {
    let records = [];
    if (dog.trainingHoursCount > 0) {
      records.push(`${dog.trainingHoursCount} training hours`);
    }
    if (dog.deploymentCount > 0) {
      records.push(`${dog.deploymentCount} deployment${dog.deploymentCount !== 1 ? 's' : ''}`);
    }
    if (dog.vetVisitCount > 0) {
      records.push(`${dog.vetVisitCount} vet visit${dog.vetVisitCount !== 1 ? 's' : ''}`);
    }
    return <>
      <Typography variant="subtitle1">
        Are you sure you want to delete {dog.name.toLowerCase().includes('k9') ? dog.name : `K9 ${dog.name}`}?
      </Typography>
      
      {records.length > 0 && 
      <Typography variant="subtitle2" color="error" style={{margin: '16px 0 24px 0'}}>
          <b>WARNING</b>: This dog has records which span {records.length === 1 
            ? records[0] : 
            [records.slice(0, -1).join(', '), records[records.length - 1]].join(' and ')
          }.
        This action will permanently delete all of these records and cannot be undone!
      </Typography>}

      {records.length === 0 && 
      <Typography variant="subtitle2" style={{margin: '16px 0 24px 0'}}>
        There are no training or deployment records associated with this dog.
      </Typography>}

    </>;
  }


  const deleteDog = () => {
    confirmDelete()
      .then(response => {
        if (response) {
          doDelete();
        }
      });
  }


  const doDelete = () => {
    setLoading(true);
    axios
      .delete('/dogs/' + dog.id)
      .then((response) => {
        if (response.status === 200) {
          const dogList = response.data; 
          enqueueSnackbar(`Dog deleted`, {
            variant: 'info'
          });
          setTimeout(() => {
            props.setDogs(dogList);
          });
        } else {
          setLoading(false);
        }
      })
  }


  return <>
    {dog && 
    <div data-testtype='dog-view' data-testid={dog.id}>
      <FeatureBox className={classes.root} loading={loading}>
        <Grid container spacing={4} direction="row">
          <Grid item xs={12} md={4}>
            <Box 
              display="flex" 
              className={classes.mainSection}
            >
              {avatar}
              <Box 
                display="flex" 
                flexDirection="column"
                className={classes.mainSectionText}
              >
                <div className='flex-hc'>
                  <Typography
                    variant="h6"
                    style={{lineHeight: '1'}}>
                    <b>{dog.name.toLowerCase().includes('k9') ? dog.name : `K9 ${dog.name}`}</b>
                  </Typography>
                  {dog.isDefault && 
                  <Tooltip
                    title={`${dog.name} is ${props.userId ? 'their' : 'my'} default dog`}
                    placement={'right-start'}
                    arrow
                  >
                    <span style={{display: 'flex'}}><Icon type='default-dog' className={classes.defaultDogIcon} /></span>
                  </Tooltip>}
                </div>
                <Typography variant="body1" className={classes.subTitle}>{dog.type}</Typography>
                <Chip
                  label={dog.dateRetired && !isFuture(dog.dateRetired) ? 'Retired' : 'Active'}
                  size="small"
                  className={`${classes.chip} ${dog.dateRetired && !isFuture(dog.dateRetired) ? 'chip-outdated chip-font-outdated' : 'chip-ok chip-font-ok'}`}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box display="flex" flexDirection="column" alignItems="left" className={classes.info}>
              
              {/* section 1 */}
              <Box display="flex" flexDirection="row" mb={3}>
                <Box mr={2}><Icon type="dog-profile" /></Box>
                <Box>
                  <div data-testid="dog-name">{dog.name}</div>
                  <div>{dog.breed}</div>
                  <div>{dog.type}</div>
                  {dog.dateBirth 
                    ? <div>
                        <DateDistance
                          dateStart={dog.dateBirth}
                          tooltipPrefix={dog.dateBirth ? "Born " : ''}
                          suffix="old"
                          options={{roundingMethod: 'floor'}}
                        />
                      </div>
                    : ''}
                </Box>
              </Box>

              {/* section 2 */}
              <Box display="flex" flexDirection="row" mb={3}>
                <Box mr={2}><Icon type="attributes" /></Box>
                <Box>                  
                  <div style={{paddingLeft: '1em', textIndent: '-1em'}}>
                    PATROL TYPES:
                    {dog.patrolTypes.length > 0
                      ? ' ' + listAttributes(patrolTypes, dog.patrolTypes) 
                      : ' N/A'}
                    </div>
                  <div style={{paddingLeft: '1em', textIndent: '-1em'}}>
                    DETECTION ODORS: 
                    {dog.detectionOdors.length > 0
                      ? ' ' + listAttributes(odorTypes, dog.detectionOdors) 
                      : ' N/A'}
                  </div>
                </Box>
              </Box>

              {/* section 3 */}
              <Box display="flex" flexDirection="row" mb={3}>
                <Box mr={2}><Icon type="stats" /></Box>
                <Box>
                  <div>
                    {displayRecordsRange(dog.dateStarted, dog.dateRetired)}
                  </div>
                  <div>{dog.trainingHoursCount} Training Hours</div>
                  <div>{dog.deploymentCount} Deployment{dog.deploymentCount !== 1 ? 's' : ''}</div>
                  <div>{dog.vetVisitCount} Vet Visit{dog.vetVisitCount !== 1 ? 's' : ''}</div>
                </Box>
              </Box>

              {/* section 4 */}
              {!isReadOnly &&
              <Box display="flex" flexDirection="row">
                <Box mr={2}><Icon type="user-guide" /></Box>
                <Box>
                  <MuiButton 
                    className={`${classes.docsButton} ${classes.info}`}
                    onClick={() => performAction('manage-documents')}
                  >
                    {(!dog.documents || dog.documents.length === 0) 
                      ? '0 K9 Documents'
                      : `${dog.documentCount} K9 Document${dog.documentCount !== 1 ? 's' : ''} in ${dog.documents.length} categor${dog.documents.length === 1 ? 'y' : 'ies'}`
                    }
                  </MuiButton>
                </Box>
              </Box>}

            </Box>
          </Grid>
        </Grid>
        {!isReadOnly &&
        <Box className={classes.floating}>
          <EditButton
            minBreakpoint='xs'
            iconOnly
            onClick={(e) => {
              e.stopPropagation();
              performAction('edit-dog');
            }}
            data-testid='edit-dog-button'
          /> 

          <DropMenu
            id={`${dog.id}-menu`}
            label={(
              <IconButton data-testtype='dog-menu-button' size="large">
                <Icon type='menu' />
              </IconButton>
            )}
            aria-label="dog menu options"
            onSelect={item => performAction(item.key)}
            items={[
              {
                key: 'edit-dog',
                name: 'Details',
                icon: 'edit'
              },
              {
                key: 'edit-photo',
                name: 'Photo',
                icon: 'photo',
              },
              {
                key: 'vaccinations',
                name: 'Vaccinations',
                icon: 'vaccination',
              },
              {
                key: 'manage-documents',
                name: 'Uploaded Documents',
                icon: 'user-guide'
              },
              {
                key: 'make-default',
                name: 'Make Default Dog',
                icon: 'default-dog',
                disabled: dog.isDefault || dog.dateRetired != null,
              },
              {
                key: 'delete-dog',
                name: 'Delete Dog ...',
                icon: 'delete'
              }
            ]}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          />
        </Box>}
      </FeatureBox>
    </div>}
    {dog && (
      <>
        <ConfirmByTypingDialog
          data-testid="deletedog-dialog"
          title="Confirm Delete Dog"
          titleStyle="error"
          titleIcon="warning"
          content={confirmDeleteText()}
          requireTypedText="DELETE"
          setConfirm={setConfirmDelete}
        />          
      </>
    )
  }
  </>;
}